import * as React from 'react'
import {
  Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {sectionCardSx, headerCellSx, cellSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE, withOpacity} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {int, kg, pct} from '../format'
import {combine, expandSelection} from './catalogueMath'
import KeyValue from '../components/KeyValue'

/**
 * Resolves the merchant's ticked `codes` to live SKU records, pulling in any
 * code's existing children via catalogueMath's own `expandSelection` — the
 * SAME expansion `applyMerge` performs on submit — so this preview can never
 * show fewer rows than the merge it is about to make.
 */
export function resolveMergeList(skus, codes) {
  const byCode = new Map((skus || []).map((s) => [s.sku, s]))
  return expandSelection(skus, codes || []).map((c) => byCode.get(c)).filter(Boolean)
}

/** Ships-desc copy of a list — the top-shipping SKU is the sensible default mother. */
export function sortByShipsDesc(list) {
  return (list || []).slice().sort((a, b) => (b.ships || 0) - (a.ships || 0))
}

/**
 * The drawer's before -> after preview. `before` is the mother's OWN record,
 * untouched; `after` is `combine(list)` — catalogueMath's own function, the
 * one `rollUp` (and therefore the Catalogue table) also calls on read.
 * Nothing here recomputes a modal weight, a spread or a weighted mean by
 * hand: a drawer that disagreed with the table on these numbers would be a
 * second, divergent merge implementation, exactly what Section 0's
 * extraction of `combine` exists to rule out.
 */
export function mergePreview(list, motherCode) {
  const before = (list || []).find((s) => s.sku === motherCode) || null
  const after = combine(list || [])
  return {before, after}
}

/** Singular/plural spelled out, never a naive `+ 's'` — matches SopPage's own levelCoverage convention. */
function extraCodesNotice(n) {
  return n === 1
    ? '1 further code was already mapped to one of your selections, so it comes along and everything ends up under a single mother.'
    : `${int(n)} further codes were already mapped to one of your selections, so they come along and everything ends up under a single mother.`
}

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}

/**
 * The merge drawer — Catalogue Task 5. `codes` is whatever the merchant
 * ticked (a selection-bar merge, or a suggested-merge pair from
 * MergeSuggestions); `skus` is the live store array threaded down from
 * CataloguePage. Every weight/shipment number this drawer shows is derived
 * on the fly from `skus` via catalogueMath — nothing is held in the
 * drawer's own state except which SKU is currently chosen as mother, so the
 * preview can never drift from what the table would show right after the
 * merge lands.
 *
 * Keyboard-dismissable and focus-return are both handled by MUI's underlying
 * Modal (Escape key, backdrop click) — see disputes-v2/DisputeDrawer.js for
 * the same note. The one gap that needs an explicit fallback is the case
 * where submitting the merge clears the selection that produced the button
 * that opened this drawer; CataloguePage's own `onClose` handles that (see
 * its own comment) by focusing the active rail item when this drawer leaves
 * focus on `<body>`.
 */
export default function MergeDrawer({open, skus, codes, onClose, onMerge}) {
  const list = React.useMemo(() => resolveMergeList(skus, codes), [skus, codes])
  const sorted = React.useMemo(() => sortByShipsDesc(list), [list])
  const defaultMother = sorted.length ? sorted[0].sku : null

  const [mother, setMother] = React.useState(defaultMother)
  // Reset the mother pick to the default (top-shipping SKU) every time the
  // drawer transitions from closed to open, rather than carrying over a
  // stale pick from a previous time this same component instance was shown
  // — adjusted during render on the `open` transition, the documented React
  // pattern for resetting state from a prop change without an Effect
  // round-trip (see https://react.dev/learn/you-might-not-need-an-effect).
  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setMother(defaultMother)
  }

  // Falls back to the default whenever the current pick isn't (or is no
  // longer) one of the resolved rows — e.g. the very first render, or `codes`
  // changing under an already-open drawer.
  const effectiveMother = list.some((s) => s.sku === mother) ? mother : defaultMother
  const {before, after} = mergePreview(list, effectiveMother)
  const kids = list.filter((s) => s.sku !== effectiveMother)
  const extra = list.length - (codes || []).length

  const handleSubmit = () => {
    if (!effectiveMother || kids.length === 0) return
    onMerge(effectiveMother, kids.map((s) => s.sku))
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 660}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                Merge under a mother SKU
              </Typography>
              <Typography sx={labelSx}>{int(list.length)} SKUs selected</Typography>
            </Box>
            <IconButton onClick={onClose} aria-label='Close merge drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          <Alert severity='info' sx={alertSx}>
            Merge codes that are the same physical item written differently. The child codes keep shipping and
            keep their own history, and their shipments and weights roll up into the mother so the freeze is
            decided on the full picture.
          </Alert>

          {extra > 0 && (
            <Alert severity='warning' sx={alertSx}>{extraCodesNotice(extra)}</Alert>
          )}

          <TextField
            select
            size='small'
            label='Mother SKU'
            value={effectiveMother || ''}
            onChange={(e) => setMother(e.target.value)}
            helperText='The code the merged item is measured and frozen under.'
            sx={{fontFamily: FONT, fontSize: SIZE.xs}}
          >
            {sorted.map((s) => (
              <MenuItem key={s.sku} value={s.sku}>{s.sku} ({int(s.ships)} shipments)</MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography sx={{...labelSx, fontWeight: WEIGHT.semibold, textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.5}}>
              Selected SKUs
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>SKU code</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Shipments</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Most probable</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Spread</TableCell>
                  <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Dead weight</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((s) => (
                  <TableRow key={s.sku}>
                    <TableCell sx={cellSx}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                        <Typography component='span' sx={{fontFamily: 'monospace', fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], color: NAVY, wordBreak: 'break-all'}}>
                          {s.sku}
                        </Typography>
                        {s.sku === effectiveMother && (
                          <Chip
                            size='small'
                            label='Mother'
                            sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: '0.6rem', height: 18, bgcolor: withOpacity(PRIMARY_BLUE, 0.12), color: PRIMARY_BLUE}}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>{int(s.ships)}</TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>{kg(s.mpw)}</TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>{pct(s.spread)}</TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>{kg(s.deadKg)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={sectionCardSx}>
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 1}}>
              {effectiveMother} after the merge
            </Typography>
            <KeyValue items={[
              {label: 'Shipments', value: `${int(before ? before.ships : 0)} → ${int(after.ships)}`},
              {label: 'Most probable weight', value: `${kg(before ? before.mpw : 0)} → ${kg(after.mpw)}`},
              {label: 'Weight spread', value: `${pct(before ? before.spread : 0)} → ${pct(after.spread)}`},
              {label: 'Dead weight, weighted by shipments', value: `${kg(before ? before.deadKg : 0)} → ${kg(after.dead)}`},
            ]} />
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.55, mt: 1.25}}>
              The most probable weight is recomputed from the combined shipment histogram, not averaged, so it
              stays a weight the courier actually billed.
            </Typography>
          </Box>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Button sx={primaryBtnSx} disabled={!effectiveMother || kids.length === 0} onClick={handleSubmit}>
            Merge {int(kids.length)} into {effectiveMother}
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
